export type ContentKind = 'review' | 'thought' | 'line';
export type CaseKey =
  | { subject: 'content'; contentKind: ContentKind; contentId: string }
  | { subject: 'reader'; authorId: string };

export interface CommunityCaseReport {
  id: string;
  reason: string;
  note: string | null;
  createdAt: string;
  snapshot: Record<string, unknown>;
  reachedFrom: { kind: ContentKind; id: string };
  reporter: {
    id: string;
    name: string | null;
    username: string | null;
    reportCount: number;
    reportsLast30Days: number;
    dismissedCount: number;
    actionedCount: number;
  };
}
export interface CommunityCase {
  key: string;
  case: CaseKey;
  pendingCount: number;
  previouslyResolvedCount: number;
  firstReportedAt: string;
  lastReportedAt: string;
  content: {
    kind: ContentKind;
    id: string;
    state: 'public' | 'not_public' | 'removed' | 'deleted';
    current: {
      body: string;
      containsSpoilers: boolean;
      rating: number | null;
      bookTitle: string | null;
      lineText: string | null;
    } | null;
    removedAt: string | null;
    restorable: boolean;
  } | null;
  author: {
    id: string;
    name: string | null;
    username: string | null;
    avatarUrl: string | null;
    avatarVersion: string | null;
    restriction: { endsAt: string | null } | null;
    actionedReportCount: number;
    removedContentCount: number;
  } | null;
  reports: CommunityCaseReport[];
}
export interface CommunityCaseQueue {
  totalCases: number;
  totalPendingReports: number;
  items: CommunityCase[];
  nextCursor: string | null;
}
export interface ModerationAction {
  id: string;
  action: 'dismiss' | 'remove_content' | 'restore_content' | 'restrict_author' | 'lift_restriction';
  createdAt: string;
  moderatorName: string | null;
  content: { kind: ContentKind; id: string } | null;
  subject: { id: string; name: string | null; username: string | null } | null;
  reportCount: number;
  details: Record<string, unknown>;
  note: string | null;
  removalOpen: boolean;
}
export interface ActiveRestriction {
  userId: string;
  name: string | null;
  username: string | null;
  startsAt: string;
  endsAt: string | null;
  moderatorName: string | null;
}

/** null = permanent. */
export type RestrictionChoice = 7 | 30 | 90 | null;
export type CaseDecision =
  | { kind: 'dismiss' }
  | { kind: 'remove' }
  | { kind: 'restrict'; days: RestrictionChoice }
  | { kind: 'remove_and_restrict'; days: RestrictionChoice };

/** The API body for POST /admin/community/cases/decision. */
export function caseDecisionBody(key: CaseKey, decision: CaseDecision, note: string) {
  const trimmed = note.trim();
  const common = { case: key, ...(trimmed ? { note: trimmed } : {}) };
  switch (decision.kind) {
    case 'dismiss':
      return { ...common, action: 'dismiss' as const };
    case 'remove':
      return { ...common, action: 'enforce' as const, removeContent: true };
    case 'restrict':
      return {
        ...common,
        action: 'enforce' as const,
        restriction: { durationDays: decision.days },
      };
    case 'remove_and_restrict':
      return {
        ...common,
        action: 'enforce' as const,
        removeContent: true,
        restriction: { durationDays: decision.days },
      };
  }
}

/** Which actions a case allows: lines have no author; reader cases have no single post. */
export function availableDecisions(item: CommunityCase) {
  const contentCase = item.case.subject === 'content';
  const authored = Boolean(item.author);
  const removable = contentCase && item.content?.state === 'public';
  return {
    remove: removable,
    restrict: authored && !item.author?.restriction,
    removeAndRestrict: removable && authored && !item.author?.restriction,
    hidePhoto: item.case.subject === 'reader' && Boolean(item.author?.avatarUrl),
  };
}

export function readerLabel(reader: { name: string | null; username: string | null } | null) {
  if (!reader) return null;
  return reader.username ? `${reader.name ?? ''} @${reader.username}`.trim() : reader.name;
}

/** The text a reporter saw, when it differs from what is shown now. */
export function reportedText(report: CommunityCaseReport, current: string | null) {
  const body = typeof report.snapshot.body === 'string' ? report.snapshot.body : null;
  return body && body !== current ? body : null;
}
